import { Box, Card, CardHeader, Grid, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { CheckCircle, Mail, RefreshCcw, XCircle } from 'react-feather';
import { useParams } from 'react-router-dom';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';

// Utilities
import { isEmpty, isNull } from 'lodash';
import { useState } from 'react';
import IconFeather from 'src/components/common/Icon';
import StyledPagination from 'src/components/common/StyledPagination';
import { useMutateRecheckEmailValidation } from 'src/services/mutate';
import { useQueryListEmailValidationPeople } from 'src/services/queries';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST } from 'src/utils/constants/config';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import { renderTextOrDefault } from 'src/utils/helpers/stringHelpers';

const EmailValidation = () => {
  const { id = '' } = useParams();
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [emailIdChecking, setEmailIdChecking] = useState('');
  const { mutateAsync, isPending } = useMutateRecheckEmailValidation();
  const {
    isFetching,
    data: { data, total_pages },
  } = useQueryListEmailValidationPeople({
    peopleId: id,
    params: { limit: LIMIT_GET_LIST, offset: page - 1 },
  });
  const listEmailValidate = getArrayDefault(data);
  // .filter((email) => !email.initial);

  if (isFetching) {
    return (
      <Grid item xs={12} container spacing={3}>
        <Grid item md={6} xl={4}>
          <Skeleton variant="rounded" animation="wave" height={150} />
        </Grid>
        <Grid item md={6} xl={4}>
          <Skeleton variant="rounded" animation="wave" height={150} />
        </Grid>
        <Grid item md={6} xl={4}>
          <Skeleton variant="rounded" animation="wave" height={150} />
        </Grid>
      </Grid>
    );
  }

  const recheckEmailValidation = async (emailId: string) => {
    setEmailIdChecking(emailId);
    const response = await mutateAsync({ emailId: emailId, peopleId: id });
    const index = listEmailValidate.findIndex((emailValidation) => emailValidation?.uuid === emailId);
    if (response.status_code === SUCCESS_CODE.GET) {
      listEmailValidate[index] = response.data;
    }
  };

  return (
    <Grid item xs={12} container gap={3}>
      {listEmailValidate.map((emailValidation, index) => {
        const mappingLabelValue = [
          { label: 'Reachable', value: emailValidation.reachable },
          { label: 'Disable ', value: emailValidation.disabled },
          { label: 'Inbox Full', value: emailValidation.full_inbox },
          { label: 'Syntax Valid', value: emailValidation.valid },
          { label: 'Deliverable', value: emailValidation.deliverable },
          { label: 'Host Exists', value: emailValidation.host_exists },
          { label: 'Disposable', value: emailValidation.disposable },
          { label: 'MX Record', value: emailValidation.has_mx_records },
          { label: 'Catch All', value: emailValidation.catch_all },
          { label: 'Free', value: emailValidation.free },
          { label: 'Domain', value: emailValidation.domain },
        ];

        return (
          <Grid key={`${emailValidation.uuid}-${index}`} item md={6} xl={4}>
            <Card>
              <CardHeader
                title={
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <IconFeather icon={<Mail />} muiProps={{ fontSize: 'small' }} />
                    <Typography variant="subtitle1" color="white" align="center">
                      {emailValidation.is_safe ? 'Safe to send' : 'Not safe to send'}
                    </Typography>
                  </Stack>
                }
                sx={{
                  background: emailValidation.is_safe ? theme.palette.secondary.main : theme.palette.textTertiary.main,
                  p: 2,
                }}
                titleTypographyProps={{
                  color: 'white',
                  align: 'center',
                }}
              />
              <Box p={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color={theme.palette.textTertiary.main}>
                    Last checked {formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, emailValidation.updated_at || '')}
                  </Typography>
                  <ButtonWithLoading
                    isLoading={isPending && emailIdChecking === emailValidation.uuid}
                    disabled={isPending && emailIdChecking === emailValidation.uuid}
                    variant="outlined"
                    color="secondary"
                    endIcon={<IconFeather icon={<RefreshCcw />} />}
                    onClick={() => recheckEmailValidation(emailValidation.uuid)}
                  >
                    Re-check
                  </ButtonWithLoading>
                </Stack>
                <Box mt={3} />
                <Typography variant="h6" align="center">
                  {renderTextOrDefault(emailValidation.email)}
                </Typography>
                <Typography variant="body2" align="center" color="text.secondary">
                  {emailValidation.is_safe
                    ? 'Your email address is safe to send'
                    : 'This email address is not safe to send'}
                </Typography>
                <Box mt={3} />
                <Box bgcolor={theme.palette.background.paper2} p={1}>
                  <Grid container rowGap={1}>
                    {mappingLabelValue
                      .filter((labelValue) => !isNull(labelValue.value))
                      .map((labelValue, index) => (
                        <Grid key={`${index}-${labelValue.value}`} item xs={6}>
                          <Box width="70%">
                            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle2" color={theme.palette.textTertiary.main}>
                                {labelValue.label}
                              </Typography>
                              {typeof labelValue.value === 'string' ? (
                                <Typography variant="body2">{labelValue.value}</Typography>
                              ) : labelValue.value ? (
                                <IconFeather
                                  icon={<CheckCircle />}
                                  muiProps={{
                                    sx: { color: theme.palette.success.dark, width: '16px', height: '16px' },
                                  }}
                                />
                              ) : (
                                <IconFeather
                                  icon={<XCircle />}
                                  muiProps={{ sx: { color: theme.palette.error.main, width: '16px', height: '16px' } }}
                                />
                              )}
                            </Stack>
                          </Box>
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              </Box>
            </Card>
          </Grid>
        );
      })}
      {!isEmpty(listEmailValidate) && (
        <Grid item xs={12}>
          <Stack justifyContent="center" alignItems="center">
            <StyledPagination
              page={page}
              total_pages={total_pages}
              onChange={function (_, value: number): void {
                setPage(value);
              }}
            />
          </Stack>
        </Grid>
      )}
    </Grid>
  );
};

export default EmailValidation;
